"use client"

import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
} from "lucide-react"
import { useTheme } from "next-themes"
import { Toaster as Sonner, toast, type ToasterProps } from "sonner"
import { cn } from "@/lib/utils"

if (typeof window !== "undefined") {
  (window as unknown as { toast: typeof toast }).toast = toast
}

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      position="top-right"
      className="toaster group"
      richColors={false}
      closeButton={true}
      duration={4000}
      visibleToasts={4}
      gap={10}
      icons={{
        success: <CircleCheckIcon className="size-5 shrink-0 text-success stroke-[2.25]" />,
        info: <InfoIcon className="size-5 shrink-0 text-info stroke-[2.25]" />,
        warning: <TriangleAlertIcon className="size-5 shrink-0 text-warning stroke-[2.25]" />,
        error: <OctagonXIcon className="size-5 shrink-0 text-destructive stroke-[2.25]" />,
        loading: <Loader2Icon className="size-5 shrink-0 text-primary stroke-[2.25] animate-spin" />,
      }}
      toastOptions={{
        classNames: {
          toast: cn(
            "group pointer-events-auto flex w-full items-start gap-3 rounded-2xl border p-4 pr-9 shadow-xl backdrop-blur-xl transition-all duration-300 font-sans",
            "bg-card/98 dark:bg-card/95 text-foreground border-border/80 shadow-black/10 dark:shadow-black/50"
          ),
          title: "text-sm font-semibold tracking-tight text-foreground leading-snug",
          description: "text-xs font-normal text-muted-foreground leading-relaxed mt-1",
          actionButton: "rounded-xl font-semibold text-xs px-3 py-1.5 bg-primary text-primary-foreground hover:bg-primary/90 shadow-xs",
          cancelButton: "rounded-xl font-medium text-xs px-3 py-1.5 bg-muted text-muted-foreground hover:bg-muted/80",
          closeButton: "!left-auto !right-2.5 !top-2.5 !transform-none !border-none !bg-transparent !shadow-none !size-6 !rounded-md !text-muted-foreground/70 hover:!text-foreground hover:!bg-black/5 dark:hover:!bg-white/10 !transition-all flex items-center justify-center",
          success: "!border-emerald-500/40 !bg-gradient-to-r !from-emerald-500/[0.08] !to-card/98 !text-foreground dark:!from-emerald-950/40 dark:!to-card/95 !shadow-emerald-500/10",
          error: "!border-red-500/40 !bg-gradient-to-r !from-red-500/[0.08] !to-card/98 !text-foreground dark:!from-red-950/40 dark:!to-card/95 !shadow-red-500/10",
          warning: "!border-amber-500/40 !bg-gradient-to-r !from-amber-500/[0.08] !to-card/98 !text-foreground dark:!from-amber-950/40 dark:!to-card/95 !shadow-amber-500/10",
          info: "!border-sky-500/40 !bg-gradient-to-r !from-sky-500/[0.08] !to-card/98 !text-foreground dark:!from-sky-950/40 dark:!to-card/95 !shadow-sky-500/10",
        },
      }}
      style={
        {
          "--normal-bg": "hsl(var(--card))",
          "--normal-text": "hsl(var(--card-foreground))",
          "--normal-border": "hsl(var(--border))",
          "--border-radius": "1rem",
          zIndex: 99999,
        } as React.CSSProperties
      }
      {...props}
    />
  )
}

export { Toaster }
